// Act V scenario content — 에메랄드 심장 · 균열의 왕좌. Stages 14–15: 아르덴 → 벨라.
// The finale. 아르덴 (14) is a 해방 duel — the corrupted 파수꾼 is not an enemy
// to break but a captive to bring back; 벨라 (15) is the 최종 저지. Both are
// villain-slot in flow terms (playerDisc:"light", 후공) but 아르덴's scenes are
// about freeing him, not defeating him. Beats: othellos/CAMPAIGN_NARRATIVE_BEATS.md
// rows 14–15 (해방 / 최종 저지). Bridge + map first; stages land one at a time.
// Every narration/dialogue string is a `Loc`; names stay Korean (see act1.ts's
// comment / ./loc.ts).

import type { DialogueLine, PinNode } from "./act1.ts";
import type { Loc } from "./loc.ts";

export const ACT5_NAME: Loc = { ko: "V. 에메랄드 심장", en: "V. The Emerald Heart" };

export const ARDEN_ID = 14;
export const BELA_ID = 15;

export const ACT5_BG = "/assets/backgrounds/act-5-emerald-heart-v1.webp";
export const ACT5_MAP_BG = "/assets/world-maps/act-5-emerald-heart.webp";

export const ARDEN_PORTRAIT =
  "/assets/characters/14-arden-corrupted-guardian-rival-v1.webp";
export const BELA_PORTRAIT =
  "/assets/characters/15-vela-rift-queen-rival-v1.webp";

/** ACT4_TO_ACT5 — bridge narration after 카이로스's 사냥문. The split 방벽 opens
 *  onto the 심장부: the 봉인의 심장 beating inside a pillar of rift-light, 아르덴
 *  chained beneath it and unable to focus on the party, 벨라 waiting on the
 *  throne. Mirrors ACT3_TO_ACT4_LINES; `lastHint` points on to 아르덴 — free him
 *  before 벨라 moves. */
export const ACT4_TO_ACT5_LINES: readonly Loc[] = [
  { ko: "갈라진 방벽 너머는 심장부였다 — 에메랄드 심장.", en: "Beyond the broken wall was the heart chamber — the Emerald Heart." },
  { ko: "균열빛 기둥 한가운데에서 봉인의 심장이 느리게 뛰고 있었다.", en: "At the center of a pillar of rift-light, the Seal Heart beat slowly." },
  { ko: "그 아래, 사슬에 묶인 아르덴은 우리를 봐도 초점을 맞추지 못했다.", en: "Below it, Arden, bound in chains, couldn't focus his eyes on us even as we stood there." },
  { ko: "왕좌의 벨라가 움직이기 전에, 아르덴부터 풀어야 한다.", en: "Before Vela on the throne makes her move, we have to free Arden first." },
];

/** ACT5_MAP — two nodes on the 에메랄드 심장 map, low to high: 아르덴 chained
 *  beneath the 봉인의 심장, 벨라 on the throne above. First-pass coords over
 *  `act-5-emerald-heart.webp` (768×1344 portrait) — tune in-browser. */
export const ACT5_PINS: readonly PinNode[] = [
  { id: 14, name: "아르덴", title: "균열에 물든 파수꾼", pos: { x: 0.36, y: 0.78 }, scale: 1.1 },
  { id: 15, name: "벨라", title: "잿빛 균열의 왕비", pos: { x: 0.39, y: 0.51 }, scale: 0.65 },
];

/** STAGE14_ARDEN_MEET — the party reaches 아르덴, chained beneath the 봉인의
 *  심장. He doesn't recognise them (fragmented, corrupted speech, threat not
 *  greeting). 린 draws a DELIBERATE CONTRAST with her own KAIROS_MEET
 *  surrender, not an equivalence — she gave herself up by her own choice,
 *  talked into it; 아르덴 is fully overtaken by the 균열, no choice left.
 *  Reframes the duel as 해방, not a fight to win. No "시험" framing (dropped
 *  since 막 IV). */
export const ARDEN_MEET: readonly DialogueLine[] = [
  { speaker: "opp", name: "아르덴", text: { ko: "…침입자. 심장에, 손대게, 둘 수 없다……", en: "...Intruders. I won't, let you, touch the heart..." } },
  { speaker: "opp", name: "토르", text: { ko: "눈의 초점이 없어. 우리를 보는 게 아니야.", en: "His eyes aren't focused. He's not seeing us." } },
  { speaker: "opp", name: "린", text: { ko: "저 상태… 균열이 통째로 삼킨 거예요.", en: "That state... the rift's swallowed him whole." } },
  { speaker: "opp", name: "브람", text: { ko: "그럼 풀어주는 게 아니라, 흔들어 깨우는 거로군.", en: "So this isn't freeing him — it's shaking him awake." } },
  { speaker: "player", name: "나", text: { ko: "정신 차려! 풀어주려고 왔어.", en: "Snap out of it! I'm here to free you." } },
  { speaker: "opp", name: "아르덴", text: { ko: "물러서라…… 나는, 지킨다……", en: "Get back... I, protect it..." } },
];

/** STAGE14_ARDEN_OUTRO — the corruption breaks and 아르덴 comes back to
 *  himself, disoriented and guilt-struck over what he did while controlled —
 *  the hero waves it off the same way KAIROS_OUTRO waved off 린's guilt ("네
 *  탓이 아니야"). He names 벨라 as the last threshold, then stays behind to
 *  resume his post — 조연 stay-behind, like 니아·칼데르. `winLine` on
 *  STAGE_14 is synced to this outro's first line. */
export const ARDEN_OUTRO: readonly DialogueLine[] = [
  { speaker: "opp", name: "아르덴", text: { ko: "…시야가, 돌아온다…… 내가, 얼마나 오래……", en: "...My sight, is coming back... how long, was I..." } },
  { speaker: "opp", name: "린", text: { ko: "괜찮아요. 이제 아르덴 씨 맞아요.", en: "It's all right. You're really Arden again now." } },
  { speaker: "opp", name: "아르덴", text: { ko: "미안하다…… 기억이 전혀, 무슨 짓을 했는지……", en: "I'm sorry... I don't remember, any of what I did..." } },
  { speaker: "player", name: "나", text: { ko: "네 탓이 아니야. 지금부터 되돌리면 돼.", en: "It's not your fault. We can set it right from here." } },
  { speaker: "opp", name: "아르덴", text: { ko: "벨라는 저 위, 왕좌에 있다. 심장을 완전히 차지하기 전에 막아야 해.", en: "Vela's up there, on the throne. We have to stop her before she takes full hold of the heart." } },
  { speaker: "opp", name: "토르", text: { ko: "우리가 간다. 이게 마지막이다.", en: "We're going. This is the last one." } },
  { speaker: "opp", name: "아르덴", text: { ko: "…나는 여기 남아 적들의 추적을 막겠다. 잘, 부탁한다.", en: "...I'll stay here and hold off any pursuers. Good luck, to you all." } },
];

/** STAGE15_BELA_MEET — the throne room, the campaign finale. 벨라 is calm and
 *  genuinely convinced, not a raving villain (scenario bio: "카리스마 있고
 *  확신에 찬 최종 적대자"). Rewritten (v2) so each line answers the one before
 *  it instead of running parallel statements: 벨라 frames her plan as a favour
 *  → 린 names it back to her in one word ("빼앗는 거잖아요") → 벨라 answers that
 *  exact word → 토르 cuts through the reframing → 벨라 owns it plainly → 나
 *  echoes her own word ("그 방식") back at her. 린 stays the natural foil: her
 *  own arc (스테이지1 "빛이 흔들리지 않았어") has always been about an individual
 *  light holding its own place, the literal counter-thesis. No "시험" framing —
 *  this is open war, per LOWEN_OUTRO ("문 너머부터는 전쟁이다"). */
export const BELA_MEET: readonly DialogueLine[] = [
  { speaker: "opp", name: "벨라", text: { ko: "결국 여기까지 왔군. 아르덴마저 넘겼다니, 제법이다.", en: "So you've made it this far. Winning over even Arden — I'm impressed." } },
  { speaker: "opp", name: "벨라", text: { ko: "그런데 왜 이렇게까지 하지? 나는 너희가 잃은 걸 되찾아 주려는 것뿐인데.", en: "But why go this far? I'm only trying to give you back what you lost." } },
  { speaker: "opp", name: "린", text: { ko: "되찾아 주는 게 아니라, 우리의 미래를 빼앗는 거잖아요.", en: "You're not giving it back — you're taking our future away." } },
  { speaker: "opp", name: "벨라", text: { ko: "빼앗는 게 아니야. 흔들리는 빛은 스스로의 미래를 못 지켜.", en: "It's not taking. A light that wavers can't protect its own future." } },
  { speaker: "opp", name: "벨라", text: { ko: "그래서 내가 대신 지켜 주는 거다.", en: "So I protect it in their place." } },
  { speaker: "opp", name: "토르", text: { ko: "그래 봤자 결국 네 뜻대로 하겠다는 거잖아.", en: "No matter how you spin it, you're just doing things your way." } },
  { speaker: "opp", name: "벨라", text: { ko: "그래. 그게 내 방식이다.", en: "Yes. That is my way." } },
  { speaker: "player", name: "나", text: { ko: "그 방식, 여기서 끝내겠어.", en: "That way ends here." } },
  { speaker: "opp", name: "벨라", text: { ko: "좋다. 증명해 봐라.", en: "Very well. Prove it." } },
];

/** STAGE15_BELA_OUTRO — campaign finale (v3, user-finalized). 벨라 never
 *  concedes the philosophical point even in defeat (user: "패배 후에도 인정
 *  못해") — her last line is flat denial ("내가 틀렸을 리 없다"), not a graceful
 *  handoff; 토르 names that refusal from outside rather than her explaining it
 *  herself (user: cut lines that explain themselves). 나's rebuttal echoes her
 *  own word choice back at her ("각자의 선택") the same way earlier exchanges
 *  in BELA_MEET do. Visual payoff trimmed to one clean line (브람) instead of
 *  three characters each restating it. 린 closes on the ensemble ("다 같이" —
 *  no one ally carried it, matching "서로 다른 빛이 각자 자리를 지킬 때 잠겨" from
 *  ORNA_OUTRO); 세라 gets the final line. `winLine` on STAGE_15 is synced to
 *  this outro's first line. */
export const BELA_OUTRO: readonly DialogueLine[] = [
  { speaker: "opp", name: "벨라", text: { ko: "…아, 내 질서가, 내 질서를 무시하다니……", en: "...Ah, my order... my order, defied..." } },
  { speaker: "player", name: "나", text: { ko: "질서가 아니라 집착이었어.", en: "That wasn't order. It was obsession." } },
  { speaker: "opp", name: "벨라", text: { ko: "나는, 세상을 지키려 했을 뿐이다. 많은 선택은 혼돈을 만들 뿐이야.", en: "I only, wanted to protect the world. Too many choices only breed chaos." } },
  { speaker: "player", name: "나", text: { ko: "그 방법은 세상을 지키는 방법이 아니야. 각자의 선택은 중요한 거야.", en: "That's not how you protect the world. Everyone's choice matters." } },
  { speaker: "opp", name: "벨라", text: { ko: "…아니. 내가 틀렸을 리 없다.", en: "...No. I can't have been wrong." } },
  { speaker: "opp", name: "토르", text: { ko: "끝까지 인정을 안 하는군.", en: "Won't admit it, even now." } },
  { speaker: "opp", name: "브람", text: { ko: "야, 심장이 다시 뛰고 있어.", en: "Hey — the heart's beating again." } },
  { speaker: "opp", name: "린", text: { ko: "우리가 되돌린 거예요. 다 같이.", en: "We're the ones who set it right. All of us together." } },
  { speaker: "opp", name: "세라", text: { ko: "고생 많았어, 다들.", en: "Good work, everyone." } },
];
